import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { createGame, drawCards, playCard, getAIMove, CARD_EMOJIS } from "./engine.js";
import type { Card, HickroGame, CardColor } from "./types.js";
import ConfettiSplash from "../../lib/ConfettiSplash.js";

type Mode = "cpu" | "local" | "multiplayer";
type PlayerColor = "red" | "yellow" | "green" | "purple" | "orange" | "teal";

const COLOR_OPTIONS: PlayerColor[] = ["red", "yellow", "green", "purple", "orange", "teal"];

const CODE_WORDS = [
  "apple", "beach", "candy", "dance", "eagle", "flame", "grape", "honey",
  "ivory", "jelly", "koala", "lemon", "mango", "noble", "ocean", "pearl",
  "queen", "rain", "sugar", "tiger", "umbra", "vivid", "whale", "xenon",
  "yacht", "zebra", "bloom", "coral", "dawn", "ember", "frost", "glow",
  "haven", "iris", "jade", "kite", "lune", "mist", "nova", "opal",
];

function generateCode(): string {
  const pick = () => CODE_WORDS[Math.floor(Math.random() * CODE_WORDS.length)];
  return `${pick()}-${pick()}-${pick()}`;
}

export default function HickroPage({ hideTitle = false }: { hideTitle?: boolean }) {
  const [mode, setMode] = useState<Mode>("cpu");
  const [playerNames, setPlayerNames] = useState(["Player 1", "Player 2", "Player 3"]);
  const [game, setGame] = useState<HickroGame>(() => createGame(["Player 1", "Player 2", "Player 3"]));
  const [selectedCard, setSelectedCard] = useState<number | null>(null);
  const [declaredColor, setDeclaredColor] = useState<CardColor>("red");
  const [showColorPicker, setShowColorPicker] = useState(false);

  const [yourIndex, setYourIndex] = useState(0);

  const [mpConnected, setMpConnected] = useState(false);
  const [mpPlayerId, setMpPlayerId] = useState<1 | 2 | null>(null);
  const [roomCode, setRoomCode] = useState("");
  const [mpToken, setMpToken] = useState("");
  const [opponentName, setOpponentName] = useState("");
  const [yourTurn, setYourTurn] = useState(false);
  const [mpError, setMpError] = useState("");
  const [joinCode, setJoinCode] = useState(generateCode);
  const [p1Name, setP1Name] = useState("Player 1");
  const [p1Color, setP1Color] = useState<PlayerColor>("red");

  const pollRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const pollingRef = useRef(false);
  const roomCodeRef = useRef(roomCode);
  const mpPlayerIdRef = useRef(mpPlayerId);
  const mpConnectedRef = useRef(mpConnected);

  roomCodeRef.current = roomCode;
  mpPlayerIdRef.current = mpPlayerId;
  mpConnectedRef.current = mpConnected;

  const isCpuTurn = mode === "cpu" && game.currentPlayer !== 0 && game.winner === null;

  const status = useMemo(() => {
    if (game.winner !== null) return `${game.players[game.winner].name} wins!`;
    const p = game.players[game.currentPlayer];
    const handSize = p.hand.length;
    if (mode === "multiplayer") {
      const isMe = game.currentPlayer === (mpPlayerId ? mpPlayerId - 1 : -1);
      return isMe ? `Your turn  ·  ${handSize} card${handSize > 1 ? "s" : ""}` : `${p.name}'s turn`;
    }
    return `${p.name}'s turn  ·  ${handSize} card${handSize > 1 ? "s" : ""}`;
  }, [game, mode, mpPlayerId]);

  const isMyTurn = mode !== "multiplayer"
    ? (mode === "cpu" ? game.currentPlayer === 0 : true)
    : yourTurn;

  // ---- API helpers --------------------------------------------------------

  async function apiPost(path: string, body: object) {
    try {
      const res = await fetch(path, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(body),
      });
      return await res.json();
    } catch {
      return { type: "error", message: "Network error." };
    }
  }

  // ---- server event handling ----------------------------------------------

  function handleServerEvent(event: any) {
    switch (event.type) {
      case "opponent-joined":
        setOpponentName(event.playerName);
        setMpConnected(true);
        setMessage(`Opponent ${event.playerName} joined! Press New Game to start.`);
        break;
      case "game-state":
        setGame(event.game);
        setYourTurn(event.yourTurn);
        if (event.yourIndex !== undefined) setYourIndex(event.yourIndex);
        setMessage("");
        break;
      case "opponent-left":
        setMpConnected(false);
        setRoomCode("");
        setOpponentName("");
        setMpPlayerId(null);
        setMpToken("");
        setMessage("Opponent left the game.");
        setGame(createGame(["Player 1", "Bot 1", "Bot 2"]));
        break;
      case "error":
        setMpError(event.message);
        break;
    }
  }

  // ---- polling ------------------------------------------------------------

  const startPolling = useCallback(() => {
    if (pollingRef.current) return;
    pollingRef.current = true;
    async function poll() {
      if (!pollingRef.current) return;
      const code = roomCodeRef.current;
      const pid = mpPlayerIdRef.current;
      if (!code || !pid) { pollingRef.current = false; return; }
      try {
        const res = await fetch(`/api/poll?code=${encodeURIComponent(code)}&playerId=${pid}`);
        const data = await res.json();
        for (const event of data.events) {
          handleServerEvent(event);
          if (event.type === "opponent-left") { pollingRef.current = false; return; }
        }
      } catch { /* retry */ }
      if (pollingRef.current) pollRef.current = setTimeout(poll, 200);
    }
    poll();
  }, []);

  function stopPolling() {
    pollingRef.current = false;
    if (pollRef.current) { clearTimeout(pollRef.current); pollRef.current = null; }
  }

  useEffect(() => { return () => stopPolling(); }, []);

  // ---- game logic ---------------------------------------------------------

  const [message, setMessage] = useState("");

  function handlePlayCard(cardIdx: number) {
    if (mode === "multiplayer") {
      if (!yourTurn) return;
      const card = game.players[yourIndex].hand[cardIdx];
      if (card.color === "wild") {
        setSelectedCard(cardIdx);
        setShowColorPicker(true);
        return;
      }
      apiPost("/api/hickro/move", { code: roomCode, playerId: mpPlayerId, token: mpToken, cardIndex: cardIdx });
      return;
    }
    if (mode === "cpu" && game.currentPlayer !== 0) return;
    if (game.winner !== null) return;
    setSelectedCard(cardIdx);

    const card = game.players[game.currentPlayer].hand[cardIdx];
    if (card.color === "wild") {
      setShowColorPicker(true);
      return;
    }
    setGame((prev) => playCard(prev, cardIdx));
    setSelectedCard(null);
  }

  function handleWildPlay(color: CardColor) {
    if (selectedCard === null) return;
    if (mode === "multiplayer") {
      apiPost("/api/hickro/move", { code: roomCode, playerId: mpPlayerId, token: mpToken, cardIndex: selectedCard, declaredColor: color });
      setSelectedCard(null);
      setShowColorPicker(false);
      return;
    }
    setGame((prev) => playCard(prev, selectedCard, color));
    setSelectedCard(null);
    setShowColorPicker(false);
  }

  function handleDraw() {
    if (mode === "multiplayer") {
      if (!yourTurn) return;
      apiPost("/api/hickro/draw", { code: roomCode, playerId: mpPlayerId, token: mpToken });
      return;
    }
    if (mode === "cpu" && game.currentPlayer !== 0) return;
    if (game.winner !== null) return;
    setGame((prev) => drawCards(prev, 1));
  }

  function handleModeChange(next: Mode) {
    if (mode === "multiplayer") {
      if (roomCode && mpToken) apiPost("/api/leave", { code: roomCode, playerId: mpPlayerId, token: mpToken });
      stopPolling();
    }
    if (next === "cpu") {
      setPlayerNames(["Player 1", "Bot 1", "Bot 2"]);
      setGame(createGame(["Player 1", "Bot 1", "Bot 2"]));
    } else if (next === "local") {
      setPlayerNames(["Player 1", "Player 2", "Player 3"]);
      setGame(createGame(["Player 1", "Player 2", "Player 3"]));
    }
    setMode(next);
    setSelectedCard(null);
    setShowColorPicker(false);
    setMessage("");
  }

  function resetGame() {
    if (mode === "multiplayer") {
      apiPost("/api/hickro/reset", { code: roomCode, playerId: mpPlayerId, token: mpToken });
      return;
    }
    setGame(createGame(playerNames));
    setSelectedCard(null);
    setShowColorPicker(false);
  }

  // ---- CPU turn -----------------------------------------------------------

  useEffect(() => {
    if (!isCpuTurn) return;
    const timer = window.setTimeout(() => {
      setGame((prev) => {
        if (prev.winner !== null) return prev;
        const move = getAIMove(prev);
        if (move === null) {
          return drawCards(prev, 1);
        }
        return playCard(prev, move.cardIndex, move.declaredColor);
      });
    }, 600);
    return () => window.clearTimeout(timer);
  }, [isCpuTurn, game.currentPlayer, game.winner]);

  // ---- multiplayer room actions -------------------------------------------

  async function handleCreateRoom() {
    const result = await apiPost("/api/create-room", {
      playerName: p1Name.trim() || "Player 1",
      playerColor: p1Color,
      gameType: "hickro",
    });
    if (result.type === "error") { setMpError(result.message); return; }
    roomCodeRef.current = result.code;
    mpPlayerIdRef.current = result.playerId;
    setRoomCode(result.code);
    setMpPlayerId(result.playerId);
    setMpToken(result.token);
    setMessage(`Room created! Share this code: ${result.code}`);
    startPolling();
  }

  async function handleJoinRoom() {
    if (!joinCode.trim()) { setMpError("Enter a room code."); return; }
    const result = await apiPost("/api/join-room", {
      code: joinCode.trim(),
      playerName: p1Name.trim() || "Player 2",
      playerColor: p1Color,
    });
    if (result.type === "error") { setMpError(result.message); return; }
    roomCodeRef.current = joinCode.trim();
    mpPlayerIdRef.current = result.playerId;
    setMpPlayerId(result.playerId);
    setMpToken(result.token);
    setRoomCode(joinCode.trim());
    setOpponentName(result.opponentName);
    setP1Color(result.opponentColor);
    setMessage(`Connected with ${result.opponentName}. Press New Game to start!`);
    startPolling();
  }

  async function handleLeaveGame() {
    await apiPost("/api/leave", { code: roomCode, playerId: mpPlayerId, token: mpToken });
    stopPolling();
    setMpConnected(false);
    setRoomCode("");
    setOpponentName("");
    setMpPlayerId(null);
    setMpToken("");
    setGame(createGame(["Player 1", "Bot 1", "Bot 2"]));
    setMessage("Left the game.");
  }

  // ---- render helpers -----------------------------------------------------

  const colorBox = (color: CardColor) => {
    const colors: Record<string, string> = { red: "#e74c3c", blue: "#3498db", green: "#2ecc71", yellow: "#f1c40f" };
    return (
      <button
        key={color}
        className="hickro-color-btn"
        style={{ background: colors[color] || "#888" }}
        onClick={() => { handleWildPlay(color); }}
      />
    );
  };

  function isHiddenCard(card: Card): boolean {
    return card.value === "hidden";
  }

  // ---- main render --------------------------------------------------------

  return (
    <div className="game-shell">
      <ConfettiSplash active={game.winner !== null} winnerName={game.winner !== null ? game.players[game.winner].name : undefined} />
      {!hideTitle && <h1 className="title">Hickro</h1>}

      <section className="control-panel">
        <div className="row two-col">
          <label>
            Mode
            <select value={mode} onChange={(e) => handleModeChange(e.target.value as Mode)}>
              <option value="cpu">vs Bots</option>
              <option value="local">Local Multiplayer</option>
              <option value="multiplayer">Multiplayer Online</option>
            </select>
          </label>
        </div>

        {mode === "local" && (
          <div className="row three-col">
            {[0, 1, 2].map((i) => (
              <label key={i}>Player {i + 1} <input value={playerNames[i]} onChange={(e) => {
                const n = [...playerNames]; n[i] = e.target.value; setPlayerNames(n);
              }} maxLength={16} /></label>
            ))}
          </div>
        )}

        {mode === "multiplayer" && !mpConnected && (
          <div className="row two-col">
            <label>
              Your Name
              <input value={p1Name} onChange={(e) => setP1Name(e.target.value)} maxLength={20} />
            </label>
            <label>
              Your Color
              <select value={p1Color} onChange={(e) => setP1Color(e.target.value as PlayerColor)}>
                {COLOR_OPTIONS.map((c) => (<option key={c} value={c}>{c.charAt(0).toUpperCase() + c.slice(1)}</option>))}
              </select>
            </label>
          </div>
        )}

        {mode === "multiplayer" && !mpConnected && !roomCode && (
          <div className="row two-col">
            <label>
              Room Code
              <input value={joinCode} onChange={(e) => setJoinCode(e.target.value)} placeholder="e.g. apple-blue-rain" maxLength={40} />
            </label>
          </div>
        )}

        {mode === "multiplayer" && !mpConnected && !roomCode && (
          <div className="mp-actions" style={{ display: 'flex', gap: '8px', justifyContent: 'center' }}>
            <button className="mp-btn" onClick={handleCreateRoom}>Create Room</button>
            <button className="mp-btn mp-btn-secondary" onClick={handleJoinRoom}>Join Room</button>
          </div>
        )}

        {mode === "multiplayer" && roomCode && !mpConnected && (
          <div style={{ textAlign: 'center' }}>
            <p className="mp-label">Share this code with a friend:</p>
            <p className="mp-code">{roomCode}</p>
            <p className="mp-waiting">Waiting for opponent to join...</p>
            <button className="mp-cancel" onClick={handleLeaveGame}>Cancel</button>
          </div>
        )}

        {mode === "multiplayer" && mpConnected && (
          <div className="mp-connected-bar">
            <span className="mp-connected-info">Connected with <strong>{opponentName}</strong></span>
          </div>
        )}

        <div className="actions">
          {(mode !== "multiplayer" || mpConnected) && <button onClick={resetGame}>New Game</button>}
          {mode === "multiplayer" && mpConnected && <button className="mp-leave" onClick={handleLeaveGame}>Leave Game</button>}
        </div>

        {mpError && <p className="subtle mp-error">{mpError}</p>}
        {message && mode !== "multiplayer" && <p className="subtle">{message}</p>}
      </section>

      {(mode !== "multiplayer" || mpConnected) && (
      <section className="board-panel">
        <p className="status">{status}</p>

        {game.topCard && (
          <div className="hickro-discard-area">
            <p className="hickro-discard-label">Discard Pile</p>
            <div className={`hickro-card hickro-${game.topCard.color} ${game.topCard.color === "wild" ? "hickro-wild" : ""}`}>
              <span className="hickro-card-value">{isHiddenCard(game.topCard) ? "?" : CARD_EMOJIS[String(game.topCard.value)]}</span>
            </div>
          </div>
        )}

        {game.players.map((player, pi) => {
          const isCurrent = pi === game.currentPlayer;
          const isMe = mode !== "multiplayer"
            ? (mode === "cpu" ? pi === 0 : true)
            : pi === (mpPlayerId ? mpPlayerId - 1 : -1);
          const showHidden = mode === "multiplayer" ? !isMe : mode === "cpu" ? pi !== 0 : false;

          return (
            <div key={pi} className={`hickro-player-hand ${isCurrent ? "hickro-active" : ""}`}>
              <p className="hickro-player-name">
                {player.name} ({player.hand.length})
                {mode === "multiplayer" && isMe && " (You)"}
              </p>
              <div className="hickro-cards">
                {player.hand.map((card, ci) => {
                  if (showHidden || isHiddenCard(card)) {
                    return <div key={ci} className="hickro-card hickro-hidden" />;
                  }
                  const canClick = isCurrent && !showHidden && game.winner === null;
                  return (
                    <button
                      key={card.id}
                      className={`hickro-card hickro-${card.color} ${card.color === "wild" ? "hickro-wild" : ""} ${!canClick ? "hickro-disabled" : ""}`}
                      onClick={() => canClick && handlePlayCard(ci)}
                      disabled={!canClick}
                    >
                      <span className="hickro-card-value">{CARD_EMOJIS[String(card.value)]}</span>
                    </button>
                  );
                })}
              </div>
            </div>
          );
        })}

        {showColorPicker && (
          <div className="hickro-color-picker">
            <p>Choose a color:</p>
            <div className="hickro-color-options">
              {(["red", "blue", "green", "yellow"] as CardColor[]).map(colorBox)}
            </div>
          </div>
        )}

        {(mode !== "multiplayer" ? isCpuTurn === false && game.winner === null : yourTurn) && !showColorPicker && (
          <div className="actions">
            <button onClick={handleDraw}>Draw Card{game.pendingDraw > 0 ? ` (${game.pendingDraw})` : ""}</button>
          </div>
        )}

        <div className="how-to-play">
          <p className="how-title">How to Play</p>
          <p>1. Match the top card by color or number.</p>
          <p>2. Action cards: Skip, Reverse, Draw 2, Wild, Wild Draw 4.</p>
          <p>3. First to empty their hand wins!</p>
        </div>
      </section>
      )}
    </div>
  );
}
